<template>
  <v-dialog fullscreen v-model="dialog" transition="dialog-bottom-transition">
    <v-card v-if="media">
      <item-fullscreen-toolbar
        :title="media.title"
        :muser="media.muser"
        :mdate="media.updated_at"
        close-route="mediaList"
      ></item-fullscreen-toolbar>
      <v-container fluid grid-list-xl v-if="media.status !== 'pending'">
        <v-layout row wrap>
          <v-flex xs12 lg4 xl3>
            <v-form ref="form" v-model="validMedia">
              <v-text-field
                v-model="media.title"
                :hint="$t('media.item.hint1')"
                :label="$t('media.item.titolo')"
                :readonly="!isProprietary && !isAdmin"
                :rules="[$validate.required]"
              />
              <v-text-field
                v-model="media.description"
                :hint="$t('media.item.hint2')"
                :label="$t('media.item.descrizione')"
                :readonly="!isProprietary && !isAdmin"
                :rules="[$validate.required]"
              />
              <media-reference
                v-if="media.resource_type === 'video' && currentUserCanEdit"
                :type="['image']"
                class="mb-4"
                :label="$t('media.item.copertina')"
                v-model="media.video_poster"
                required
                @mediareference:invalid="validPoster = false"
                @mediareference:valid="validPoster = true"
              />
              <v-combobox
                class="mb-4"
                v-model="media.tags"
                multiple
                chips
                deletable-chips
                :hint="$t('media.item.msg1')"
                persistent-hint
                label="Tag"
                :disabled="!isProprietary && !isAdmin"
              />
              <v-select
                v-model="media.license"
                :label="$t('media.item.licenza')"
                :items="licenses"
                :disabled="!isProprietary && !isAdmin"
                :rules="[$validate.required]"
              />
              <v-text-field
                v-model="media.author"
                :readonly="!isProprietary && !isAdmin"
                :label="$t('media.item.autore')"
              />
              <v-textarea
                v-model="media.note"
                :label="$t('media.item.note')"
                :readonly="!isProprietary && !isAdmin"
                auto-grow
              />
              <div class="pt-4" v-if="isProprietary || isAdmin">
                <v-btn class="ml-0" color="primary" :disabled="!validForm" @click="save">{{
                  $t('media.item.cta1')
                }}</v-btn>
                <v-btn
                  flat
                  class="bold-text secondary--text text--darken-2 ml-0"
                  @click="confirm = true"
                  >{{ $t('media.item.cta2') }}</v-btn
                >
              </div>
            </v-form>
          </v-flex>
          <v-flex xs12 lg5 xl4>
            <v-responsive class="grey lighten-2 imageContainer">
              <cloudinary-image
                v-if="media.resource_type === 'image'"
                :id="media.public_id"
                :type="media.resource_type"
                :version="media.version"
                transformation="h_800,q_auto,f_jpg"
              />
              <video
                controls
                :src="media.secure_url"
                v-if="media.resource_type === 'video'"
                width="100%"
              />
            </v-responsive>
            <v-img height="400px" v-if="media.resource_type === 'raw'" class="accent">
              <v-layout fill-height align-center justify-center>
                <v-flex class="text-xs-center">
                  <v-icon large>attach_file</v-icon>
                  <div class="font-weight-black text--white">{{ media.format }}</div>
                </v-flex>
              </v-layout>
            </v-img>
            <div class="mt-2 mb-3">
              <span class="mt-2 font-weight-bold body-2 grey--text">
                <media-icon :mediaFormat="media.format" :mediaType="media.resource_type" />
                {{ media.format }}
                <span v-if="media.resource_type === 'image'"
                  >{{ media.width }}&times;{{ media.height }} -</span
                >
                {{ media.bytes | bytes }} - {{ $t('media.item.caricato') }}
                {{ media.created_at | formatDate('dd/MM/Y - HH:mm') }} {{ $t('media.item.da') }}
                {{ media.cuser }}
                <span v-if="isAdmin"> Proprietario: {{ media.proprietary }} </span>
                <div v-if="isAdmin" class="mt-3">
                  <v-icon
                    class="mr-2"
                    @click="copyToClipboard($cloudinaryUrl(media.resource_type, media.public_id))"
                  >
                    far fa-copy</v-icon
                  >
                  <a
                    @click="copyToClipboard($cloudinaryUrl(media.resource_type, media.public_id))"
                    >{{ $t('media.item.copyurl') }}</a
                  >
                </div>
                <div>
                  <v-icon class="mr-2" @click="copyToClipboard(media.public_id.split('/')[1])">
                    far fa-copy</v-icon
                  >
                  <a @click="copyToClipboard(media.public_id.split('/')[1])">{{
                    $t('media.item.copyid')
                  }}</a>
                </div>
                <div v-if="isAdmin">
                  <a
                    :href="$cloudinaryUrl(media.resource_type, media.public_id)"
                    download
                    target="_blank"
                  >
                    <v-icon class="mr-2">fas fa-download</v-icon>{{ $t('media.item.scarica') }}
                  </a>
                </div>
                <div v-if="isProprietary || isAdmin">
                  <!-- type, id, transformation, version)-->
                  <a href v-if="currentUserCanEdit">
                    <v-icon class="mr-2">fas fa-exchange-alt</v-icon>
                    <label for="file">{{ $t('media.item.sostituisci') }}</label>
                  </a>
                  <input
                    type="file"
                    id="file"
                    :disabled="!currentUserCanEdit"
                    :accept="'.' + media.format"
                    @change="changeFile($event)"
                  />
                </div>
                <div v-if="isAdmin" class="mt-3">
                  <v-icon class="mr-2" @click="transferProp"> far fa-copy</v-icon>
                  <a @click="openDialog = true">{{ $t('media.item.trasferisci') }}</a>
                </div>
              </span>
            </div>
            <div class="mt-2 mb-4" v-if="media.resource_type === 'image' && media.format !== 'pdf'">
              <h3 class="grey--text text--darken-3">
                <v-icon class="mr-2">fas fa-images</v-icon>{{ $t('media.item.preview') }}
              </h3>
              <span v-html="$t('media.item.msg2')"></span>
              <v-layout align-space-between justify-space-between wrap fill-height>
                <v-flex sm12 md8 pl-md-0>
                  <image-crop
                    v-model="media.coordinates['intestazione_sinistra_4_3']"
                    :img="imageUrl"
                    :aspect-ratio="4 / 3"
                    :id="media.public_id"
                    :type="media.resource_type"
                    :version="media.version"
                    transformation="h_300,ar_4:3,c_fill,q_auto,f_jpg,c_thumb,g_faces"
                    :enforce-ratio="true"
                  >
                    <template v-slot:overimage>
                      <div class="preview-4-3-placeholder-box">
                        <svg width="100%" height="100%" viewBox="0 0 500 380">
                          <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            clip-path="url(#clip-path-4-3)"
                            style="fill: #ddd"
                          />
                          <defs>
                            <clipPath id="clip-path-4-3">
                              <rect x="38" y="158" rx="5" ry="5" width="425" height="19" />
                              <rect x="38" y="216" rx="5" ry="5" width="425" height="19" />
                              <rect x="38" y="273" rx="5" ry="5" width="425" height="19" />
                              <rect x="38" y="100" rx="5" ry="5" width="143" height="19" />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    </template>
                  </image-crop>
                </v-flex>
                <v-flex sm12 md4 pr-md-0>
                  <image-crop
                    v-model="media.coordinates['16_9']"
                    :img="imageUrl"
                    :aspect-ratio="16 / 9"
                    :id="media.public_id"
                    :type="media.resource_type"
                    :version="media.version"
                    transformation="h_300,ar_16:9,c_fill,q_auto,f_auto"
                    :hide-default-action-button="true"
                    class="preview-16-9-box"
                  >
                    <template v-slot:overimage>
                      <div class="preview-16-9-over"></div>
                    </template>
                    <template v-slot:underimage="{ on }">
                      <v-layout ma-0 column fill-height class="preview-16-9-placeholder-box">
                        <svg width="100%" height="100%" viewBox="0 0 500 300">
                          <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            clip-path="url(#clip-path-16-9)"
                            style="fill: #ddd"
                          />
                          <defs>
                            <clipPath id="clip-path-16-9">
                              <rect x="38" y="98" rx="5" ry="5" width="425" height="19" />
                              <rect x="38" y="156" rx="5" ry="5" width="425" height="19" />
                              <rect x="38" y="213" rx="5" ry="5" width="425" height="19" />
                              <rect x="170" y="40" rx="5" ry="5" width="143" height="19" />
                            </clipPath>
                          </defs>
                        </svg>
                        <v-flex class="text-xs-right">
                          <v-btn color="primary" @click="on">{{ $t('media.item.cta3') }}</v-btn>
                        </v-flex>
                      </v-layout>
                    </template>
                  </image-crop>
                </v-flex>
              </v-layout>
            </div>
            <div v-if="media.references.length">
              <v-list subheader>
                <v-subheader class="pa-0">
                  <v-icon aria-hidden="true" class="label-icon mr-2">account_tree</v-icon>
                  {{ $t('media.item.msg3') }}
                </v-subheader>
                <template v-for="(r, index) in media.references">
                  <v-list-tile :key="index">
                    <v-list-tile-content>
                      <v-list-tile-title>{{ r.title[r.default_lang] }}</v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-divider :key="index + 'd'" />
                </template>
              </v-list>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container fluid v-else>
        <v-layout>
          <v-flex sm12 class="text-xs-center mt-4">
            <v-progress-circular indeterminate color="accent" />
            <h2>{{ $t('media.item.msg4') }}</h2>
          </v-flex>
        </v-layout>
      </v-container>
      <!-- conferma elimina -->
      <v-dialog v-model="confirm" persistent max-width="320">
        <v-card>
          <v-card-title class="headline">
            <span v-if="!media.references.length">{{ $t('media.card.modale.sicuro') }}</span>
            <span v-else>{{ $t('media.card.modale.deny') }}</span>
          </v-card-title>
          <v-card-text>
            <span v-if="!media.references.length">{{ $t('media.card.modale.msg1') }}</span>
            <span v-else>{{ $t('media.card.modale.msg2') }}e</span>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click.native="confirm = false">
              <span v-if="!media.references.length">{{ $t('media.card.modale.annulla') }}</span>
              <span v-else>{{ $t('media.card.modale.chiudi') }}</span>
            </v-btn>
            <v-btn color="error" flat @click.native="remove" v-if="!media.references.length">{{
              $t('media.card.modale.elimina')
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- trasferisci proprietà -->
      <v-dialog v-model="openDialog" persistent max-width="320">
        <v-card>
          <v-card-title class="headline">
            <span>Trasferisci proprietà</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              label="Search user"
              @keypress="filterUserList"
              @keyup.delete="filterUserList"
              v-model="searchProp"
            ></v-text-field>
            <v-select
              v-model="selectedUser"
              :items="usersFiltered"
              required
              :label="'Seleziona utente'"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn flat @click.native="openDialog = false">
              <span v-if="!media.references.length">{{ $t('media.card.modale.annulla') }}</span>
              <span v-else>{{ $t('media.card.modale.chiudi') }}</span>
            </v-btn>
            <v-btn
              color="error"
              flat
              @click.native="transferProp(selectedUser)"
              v-if="!media.references.length"
              >Trasferisci</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { $EventBus } from '@/eventBus';
import { $cloudinaryUrl } from '@/mixins/cloudinary';
import itemFullscreenToolbar from '@/components/common/ItemFullscreenToolbar';
import cloudinaryImage from '@/components/common/CloudinaryImage';
import imageCrop from '@/components/common/ImageCrop';
import mediaIcon from '@/components/media/MediaIcon';
import mediaReference from '@/components/media/MediaReference';
import { $userCanEditMedia } from '@/mixins/media';
import _ from 'lodash';

export default {
  data: () => ({
    proprietary: null,
    searchProp: null,
    selectedUser: null,
    users: [],
    testu: [],
    usersFiltered: [],
    openDialog: false,
    logged_user_id: null, // id di chi è loggato su manifest
    cuser_email: null, // email di chi ha caricato il file
    dialog: null,
    confirm: null,
    media: null,
    validMedia: null,
    validPoster: true,
    licenses: [
      { value: 'N', text: 'All rights reserved' },
      { value: 'PD', text: 'Public domain' },
      { value: 'U', text: 'Unsplash license' },
      { value: 'A', text: 'Attribution (CC BY)' },
      { value: 'AS', text: 'Attribution-ShareAlike (CC BY-SA)' },
      { value: 'AND', text: 'Attribution-NoDerivs (CC BY-ND)' },
      { value: 'ANC', text: 'Attribution-NonCommercial (CC BY-NC)' },
      { value: 'ANCS', text: 'Attribution-NonCommercial-ShareAlike (CC BY-NC-SA)' },
      { value: 'ANCND', text: 'Attribution-NonCommercial-NoDerivs (CC BY-NC-SA)' },
    ],
  }),
  components: {
    itemFullscreenToolbar,
    cloudinaryImage,
    mediaIcon,
    mediaReference,
    imageCrop,
  },
  mixins: [$cloudinaryUrl, $userCanEditMedia],
  methods: {
    filterUserList() {
      this.usersFiltered = _.filter(this.users, (val) => {
        return _.includes(val, this.searchProp);
      });
      this.selectedUser = this.usersFiltered[0];
    },
    transferProp(email) {
      this.$http({
        method: 'patch',
        url: `/media/${this.media._id}`,
        data: {
          utente: email,
        },
      })
        .then((result) => {
          $EventBus.$emit('message', { type: 'success', text: this.$t('success.media.saved') });
          this.openDialog = false;
        })
        .catch((err) => {
          console.log('error', err);
        });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      this.$toast.open({ type: 'default', position: 'bottom', message: this.$t('info.copy') });
    },
    save() {
      this.$http({
        method: 'put',
        url: `/media/${this.media._id}`,
        data: this.media,
      })
        .then((result) => {
          this.dialog = false;
          $EventBus.$emit('message', { type: 'success', text: this.$t('success.media.saved') });
          this.$router.push({ name: 'mediaList', query: { ...this.$route.query } });
        })
        .catch((err) => {
          console.log('media update error', err);
          $EventBus.$emit('message', { type: 'error', text: this.$t('error.media.save') });
        });
    },
    remove() {
      this.confirm = false;
      this.$http
        .delete(`media/${this.$route.params.id}/${this.media.public_id}`) // mi servono entrambi, devo cancellarli sia da me che da loro
        .then((result) => {
          this.dialog = false;
          $EventBus.$emit('message', { type: 'success', text: this.$t('success.media.deleted') });
          this.$router.push({ name: 'mediaList', query: { ...this.$route.query } });
        })
        .catch((err) => {
          console.log('media delete error', err);
          $EventBus.$emit('message', { type: 'error', text: this.$t('error.media.delete') });
        });
    },
    changeFile(event) {
      const formData = new FormData();
      const file = document.querySelector('#file').files[0];
      this.$log('file -->', file);
      this.$log('media -->', JSON.stringify(this.media));
      formData.append('file', file); // manda il nuovo
      formData.append('media', JSON.stringify(this.media)); // e i dati del media
      this.$log('form data: ', formData);
      this.$http
        .put('media/replace', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          this.dialog = false;
          $EventBus.$emit('message', { type: 'success', text: this.$t('success.media.replaced') });
          this.$router.push({ name: 'mediaList', query: { ...this.$route.query } });
        })
        .catch((err) => {
          console.log('media replace error', err);
          $EventBus.$emit('message', { type: 'error', text: this.$t('error.media.replace') });
        });
    },
  },
  computed: {
    validForm() {
      return this.validMedia && this.validPoster;
    },
    currentUserCanEdit() {
      return this.$userCanEditMedia(this.$user);
    },
    imageUrl() {
      return this.$cloudinaryUrl(
        this.media.resource_type,
        this.media.public_id,
        'h_800,q_auto,f_jpg',
        this.media.version,
      ); // type, id, transformation, version)
    },
    isProprietary() {
      if (this.$user.email === this.proprietary) return true;
      else return false;
    },
    isAdmin() {
      if (this.$userHasRoles(['admin'])) return true;
      else return false;
    },
  },
  mounted() {
    this.$http
      .get(`media/${this.$route.params.id}`)
      .then((result) => {
        this.dialog = true;
        if (!result.data.coordinates) {
          result.data.coordinates = {};
        }
        this.media = result.data;
        this.$log('media: ', this.media);
        this.cuser_email = this.media.cuser_email;
        this.proprietary = this.media.proprietary;
        this.logged_user_id = this.$user.sub;
      })
      .catch((err) => {
        console.log('media get error', err);
        $EventBus.$emit('message', { type: 'error', text: 'Impossibile recuperare il media' });
      });

    if (this.$userHasRoles(['admin'])) {
      this.$http
        .get('/users/all')
        .then((response) => {
          _.forEach(response.data.users, (value) => {
            this.users.push(value.email);
          });
          this.usersFiltered = this.users;
        })
        .catch((err) => {
          console.error(err);
          $EventBus.$emit('message', { type: 'error', text: this.$t('error.users.list') });
        });
    }
  },
};
</script>

<style scoped>
.imageContainer img {
  max-width: 60%;
  max-height: 400px;
  margin: 0 auto;
  display: block;
}

input[type='file'] {
  position: absolute;
  clip: rect(0, 0, 0, 0);
}

.preview-4-3-placeholder-box {
  position: absolute;
  left: 6.21%;
  bottom: 8.27%;
  background: #fff;
  max-width: 44.4%;
  min-height: 44.9%;
}

.preview-16-9-placeholder-box {
  background: rgb(250, 250, 250);
  height: 100%;
}

.preview-16-9-over::before {
  bottom: -2px;
  border-color: transparent transparent rgb(250, 250, 250) transparent;
  z-index: 1;
}

.preview-16-9-over::after {
  bottom: 0;
  border-color: transparent transparent rgb(226, 226, 226) transparent;
}

.preview-16-9-over::after,
.preview-16-9-over::before {
  content: '';
  position: absolute;
  left: 1em;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 1em 1em 1em;
}

.preview-16-9-box {
  background: rgb(250, 250, 250);
  border: 1px solid rgb(226, 226, 226);
}

a {
  text-decoration: underline;
}

a label {
  cursor: pointer;
}
</style>
